/**
 * Unified Stream Service
 * Single source of truth for all real-time streaming
 * Based on the simple MQTT demo from backend team
 */

import { ref, computed, onUnmounted } from "vue";
import { useRouter } from "vue-router";
import mqtt, { MqttClient } from "mqtt";
import { isAxiosError } from "axios";
import { useAuthenticationStore } from "@/stores/authentication";
import { useViewContextStore } from "@/stores/viewContext";
import { getMqttUrl } from "@/composables/apiClientFactory";
import { useEdgeApi } from "@/composables/useEdgeApi";
import type { StreamState } from "@/types/streaming";
import { STREAM_TOPICS } from "@/types/streaming";
import {
    handleEdgeStreamMessage,
    recoverStreamState,
    resetStreamCursor,
    clearStreamStoreCache,
    setLastReceivedUuid,
    streamChainKey,
} from "@/composables/stream/handlers";
import type { StreamRecoverySeverity } from "@/composables/stream/handlers";

// Singleton instance management
let streamServiceInstance: ReturnType<typeof createStreamService> | null = null;

// Track if we're in the process of connecting/disconnecting to prevent race conditions
let connectionPromise: Promise<void> | null = null;

interface MqttConfig {
    username: string;
    password: string;
}

// Broker credentials are served to authenticated users only; never ship them in the bundle.
// Cached for the session; a failed fetch is dropped so the next attempt refetches.
let mqttConfigPromise: Promise<MqttConfig> | null = null;

const loadMqttConfig = (): Promise<MqttConfig> => {
    if (!mqttConfigPromise) {
        // async so a synchronous throw from API client setup becomes a rejection.
        mqttConfigPromise = (async () => {
            const { data } = await useEdgeApi().get<MqttConfig>(
                "/mqtt-config/web-ui",
            );
            return data;
        })().catch((error) => {
            mqttConfigPromise = null;
            throw error;
        });
    }
    return mqttConfigPromise;
};

const describeConfigError = (error: unknown): string => {
    if (isAxiosError(error) && error.response) {
        return `HTTP ${error.response.status}`;
    }
    return error instanceof Error ? error.message : String(error);
};

// 4xx other than timeout/rate-limit won't change on retry.
const isRetryableConfigError = (error: unknown): boolean => {
    const status = isAxiosError(error) ? error.response?.status : undefined;
    return !status || status >= 500 || status === 408 || status === 429;
};

// CONNACK refusals for bad credentials: 4/5 (MQTT 3.1.1), 134/135 (MQTT 5).
const BROKER_AUTH_ERROR_CODES = new Set([4, 5, 134, 135]);

const isBrokerAuthError = (error: Error): boolean => {
    const code = (error as Error & { code?: number }).code;
    return code !== undefined && BROKER_AUTH_ERROR_CODES.has(code);
};

const RETRY_BASE_MS = 5000;
const RETRY_MAX_MS = 60000;

function createStreamService() {
    const authStore = useAuthenticationStore();
    const mapContextStore = useViewContextStore();
    const router = useRouter();

    // State
    const client = ref<MqttClient | null>(null);
    const state = ref<StreamState>({
        isConnected: false,
        isConnecting: false,
        lastError: null,
    });

    // Diagnostic: server-side timestamp from the most recent location message
    // (user.lastLocationPost) — used to calculate end-to-end latency (User -> Backend -> Client)
    const mobileUserTimestamp = ref<number>(0);

    // Diagnostic: server-side timestamp for the MQTT message itself
    // (payload.timestamp) — used to calculate MQTT latency (Backend -> Client)
    const mqttServerTimestamp = ref<number>(0);

    // Track if event listeners are already attached to prevent duplicates
    let eventListenersAttached = false;

    // True once a session has existed, so a later 'connect' is a reconnect
    // (messages missed) rather than a first connect (caller just bootstrapped).
    let hasConnectedBefore = false;

    // Computed
    const isStreamActive = computed(
        () => state.value.isConnected || state.value.isConnecting,
    );

    /**
     * Parse message payload
     */
    const parsePayload = (payload: Buffer | string): any => {
        try {
            const text = payload.toString();
            return JSON.parse(text);
        } catch (error) {
            console.error("Failed to parse stream payload:", error);
            return payload.toString();
        }
    };

    /**
     * RAF micro-batch queue — messages are parsed immediately on the MQTT callback
     * but processing is deferred to the next animation frame. This collapses 1000+
     * synchronous handler calls/second down to ~60 flush cycles/second.
     *
     * DIAGNOSTIC MODE: To isolate whether backlog is frontend or MQTT/broker-side,
     * run this in the browser console to skip ALL rendering and just log receipt times:
     *
     *   window.__MQTT_LOG_ONLY = true   // enable - logs only, no rendering
     *   window.__MQTT_LOG_ONLY = false  // disable - back to normal
     *
     * Then have phones send updates, stop them, and watch whether logs stop
     * immediately (~5s = frontend backlog) or continue (~60s = MQTT broker backlog).
     */
    let _mqttDiagCount = 0;

    // ================================================================
    // Location coalescing — replaces the old RAF queue + array approach.
    //
    // Instead of pushing full MQTT payloads into an ever-growing array
    // and iterating them on each drain, we maintain a single Map keyed by
    // userId. Each MQTT message overwrites the previous entry for that
    // user (O(1), zero allocation). The RAF drain then reads the Map
    // directly — no intermediate arrays, no splice, no overflow risk.
    //
    // Memory is bounded: one entry per unique user, not per message.
    // With 1000 users that's ~1000 Map entries max regardless of how
    // many MQTT messages arrive between frames.
    // ================================================================
    const _latestByUser = new Map<number, any>();
    // Newest queued uuid per publisher chain; drained into the cursor per chain.
    const _lastCoalescedUuids = new Map<string, string>();
    let _rafHandle: number | null = null;

    // When the NEWEST coalesced message arrived — deliberately not when the
    // frame was scheduled. A hidden tab has its RAF throttled to zero while the
    // socket keeps delivering, so time-since-schedule there grows without bound
    // over perfectly fresh data and would discard positions milliseconds old on
    // every refocus. Time-since-newest-message only grows when nothing is
    // arriving, which is the condition actually worth discarding for: a frozen
    // tab whose socket died under it.
    let _newestCoalescedAt = 0;
    const COALESCED_STALE_MS = 1_000;

    /** Drop the queued batch and the frame it was scheduled on. */
    const _discardCoalescedLocations = () => {
        if (_rafHandle !== null) {
            cancelAnimationFrame(_rafHandle);
            _rafHandle = null;
        }
        _latestByUser.clear();
        _lastCoalescedUuids.clear();
        _newestCoalescedAt = 0;
    };

    /**
     * Drain the coalesced location map — called once per animation frame.
     * Each user appears at most once (latest position wins).
     */
    const _drainLocationUpdates = () => {
        _rafHandle = null;
        if (_latestByUser.size === 0) return;

        // Age, not transport state: on wake the browser repaints in ~16ms while
        // MQTT.js may need a 90s keepalive timeout to notice its socket died, so
        // waiting for 'close' to discard is a race we lose. A stale batch would
        // apply pre-sleep positions and rewind the cursor via setLastReceivedUuid.
        if (Date.now() - _newestCoalescedAt > COALESCED_STALE_MS) {
            console.log(
                `⏭️ Discarding ${_latestByUser.size} coalesced location update(s), newest ${Date.now() - _newestCoalescedAt}ms old`,
            );
            _latestByUser.clear();
            // The cursor is intentionally NOT advanced past the dropped batch:
            // these messages were received and then thrown away, so the chain
            // really is broken and the next message SHOULD read as a gap and
            // trigger recovery. Advancing here would suppress the refetch.
            _lastCoalescedUuids.clear();
            _newestCoalescedAt = 0;
            return;
        }

        // Snapshot and clear atomically
        const users = [..._latestByUser.values()];
        _latestByUser.clear();
        const uuids = [..._lastCoalescedUuids];
        _lastCoalescedUuids.clear();

        handleEdgeStreamMessage(
            mapContextStore,
            {
                scope: "location",
                method: "PATCH",
                deltas: { users },
            },
            router,
        );

        // Advance the UUID cursor so the next non-location message doesn't
        // trigger a false gap warning.
        for (const [chain, uuid] of uuids) {
            setLastReceivedUuid(uuid, chain);
        }
    };

    const handleMessage = (topic: string, payload: Buffer) => {
        const rawData = parsePayload(payload);

        // Capture timestamps immediately — diagnostics must not be deferred
        // 1. MQTT Server Timestamp (Backend -> Client latency)
        if (rawData?.timestamp) {
            mqttServerTimestamp.value = new Date(rawData.timestamp).getTime();
        }
        // 2. Mobile User Timestamp (User -> Backend -> Client latency)
        const userTs = rawData?.deltas?.users?.[0]?.lastLocationPost;
        if (userTs) {
            mobileUserTimestamp.value = new Date(userTs).getTime();
        }

        // --- DIAGNOSTIC MODE ---
        // Toggle with: window.__MQTT_LOG_ONLY = true/false in browser console
        // Only location/user updates are skipped - incidents, devices etc. still work.
        if ((window as any).__MQTT_LOG_ONLY) {
            const isLocationUpdate =
                rawData?.scope === "location" ||
                rawData?.deltas?.users?.length > 0;
            if (isLocationUpdate) {
                _mqttDiagCount++;
                const users = rawData.deltas?.users ?? [];
                const userSummary = users
                    .map(
                        (u: any) =>
                            `id=${u.id} lat=${u.lat ?? "?"} lng=${u.lng ?? "?"} lvl=${u.levelId ?? "?"}`,
                    )
                    .join(" | ");
                console.log(
                    `[MQTT-DIAG] #${_mqttDiagCount} | ${new Date().toISOString()} | scope=${rawData.scope} method=${rawData.method} | ${users.length} user(s): ${userSummary || "n/a"}`,
                );
                return;
            }
        }
        // --- END DIAGNOSTIC MODE ---

        // Location messages (scope=location with user deltas) are batched into the
        // RAF queue for per-user deduplication — only the latest position per user
        // matters within a frame, so we collapse 1000+ msgs/s → ~60 flushes/s.
        //
        // All other messages (incidents, devices, markers, toasts, etc.) are
        // processed synchronously right here so critical updates are never held up
        // by a 16ms animation-frame boundary.
        const isLocationMessage =
            topic === STREAM_TOPICS.edge &&
            rawData?.scope === "location" &&
            rawData?.deltas?.users?.length;

        if (isLocationMessage) {
            // Coalesce: overwrite previous entry per user (O(1), no array growth).
            if (rawData.uuid) {
                _lastCoalescedUuids.set(streamChainKey(rawData), rawData.uuid);
            }
            for (const u of rawData.deltas.users) {
                _latestByUser.set(Number(u.id), u);
            }
            _newestCoalescedAt = Date.now();

            if (_rafHandle === null) {
                _rafHandle = requestAnimationFrame(_drainLocationUpdates);
            }
            return;
        }

        // Non-location: process immediately, bridging the cursor over any
        // location batch still queued whose uuid is not recorded yet.
        //
        // The _lastCoalescedUuids match is NOT optional: advancing on
        // rawData.last_uuid unconditionally sets the cursor to the very value
        // handleEdgeStreamMessage then compares it against, which is why gap
        // detection was dead code.
        if (topic === STREAM_TOPICS.edge) {
            const chain = streamChainKey(rawData ?? {});
            if (
                rawData?.last_uuid &&
                rawData.last_uuid === _lastCoalescedUuids.get(chain)
            ) {
                setLastReceivedUuid(rawData.last_uuid, chain);
                // Spend the batch's claim on the cursor: this message advances
                // past it, and leaving the uuid would let the pending frame
                // rewind the cursor behind us and fake a gap. Positions still drain.
                _lastCoalescedUuids.delete(chain);
            }
            handleEdgeStreamMessage(mapContextStore, rawData, router);
        } else {
            console.warn(`Unknown stream topic: ${topic}`);
        }
    };

    // Wake-up recovery. The broker replays nothing (clean: true, per-session
    // clientId), so anything published while asleep or offline is gone. MQTT.js
    // restores the socket but never the state; these listeners turn "connected
    // again" into "we know what we missed".

    let wakeListenersAttached = false;

    const handleWakeup = (reason: string, severity: StreamRecoverySeverity) => {
        if (!authStore.isAuthenticated()) return;

        // No document.hidden guard here: onVisibilityChange already filters the
        // hidden case, and applying it to every caller silently dropped the
        // 'online' path — a network outage that ends while the tab is hidden is
        // exactly when a gap is certain, and nothing would retry it.
        if (!state.value.isConnected && !state.value.isConnecting) {
            // No transport. Get one back; the 'connect' handler recovers state
            // itself once it lands, so don't also refetch here.
            console.log(`👁️ ${reason} — stream is down, reconnecting`);
            void connect().catch((error) => {
                console.error(
                    "[StreamService] Wake-up reconnect failed:",
                    error,
                );
            });
            return;
        }

        // Transport is up. Refetch anyway: a frozen tab can hold a socket that
        // looks alive but delivered nothing. Throttled by the shared cooldown.
        recoverStreamState(mapContextStore, reason, severity);
    };

    const onVisibilityChange = () => {
        // Precautionary: a hidden tab still receives messages, so usually
        // nothing was missed and there is nothing to announce.
        if (!document.hidden) {
            handleWakeup("tab became visible", "precautionary");
        }
    };

    // offline→online means the network really was down and messages really were
    // lost, whether or not MQTT.js has noticed its socket is dead yet.
    const onOnline = () =>
        handleWakeup("network came back online", "stream-interrupted");

    const attachWakeListeners = () => {
        if (wakeListenersAttached) return;
        document.addEventListener("visibilitychange", onVisibilityChange);
        window.addEventListener("online", onOnline);
        wakeListenersAttached = true;
    };

    const detachWakeListeners = () => {
        if (!wakeListenersAttached) return;
        document.removeEventListener("visibilitychange", onVisibilityChange);
        window.removeEventListener("online", onOnline);
        wakeListenersAttached = false;
    };

    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let retryAttempt = 0;

    // Bumped by disconnect() so an in-flight connect() knows it was cancelled.
    let connectGeneration = 0;

    const clearRetry = () => {
        if (retryTimer !== null) {
            clearTimeout(retryTimer);
            retryTimer = null;
        }
    };

    const scheduleRetry = () => {
        clearRetry();
        // The 'online' wake listener reconnects; retrying offline only stacks error toasts.
        if (!navigator.onLine) return;
        const delay = Math.min(RETRY_BASE_MS * 2 ** retryAttempt, RETRY_MAX_MS);
        retryAttempt++;
        retryTimer = setTimeout(() => {
            retryTimer = null;
            void connect().catch((error) => {
                console.error("[StreamService] Retry failed:", error);
            });
        }, delay);
    };

    const teardownClient = () => {
        if (!client.value) return;
        client.value.removeAllListeners();
        client.value.end(true);
        client.value = null;
        eventListenersAttached = false;
        // 'close' won't fire after removeAllListeners(), so drop the queued
        // frame here. Cursor and hasConnectedBefore are kept for recovery.
        _discardCoalescedLocations();
    };

    /** Start loading broker credentials early so connect() doesn't wait on them. */
    const prefetchConfig = () => {
        if (!authStore.isAuthenticated()) return;
        // connect() reports and retries failures; nothing to do here.
        loadMqttConfig().catch(() => {});
    };

    /**
     * Connect to MQTT broker
     * Simplified like the backend demo - let MQTT.js handle complexity
     */
    const connect = async () => {
        // If already connecting/disconnecting, wait for it to complete
        if (connectionPromise) {
            await connectionPromise;
        }

        // isAuthenticated is a function on the store — calling it matters, an
        // unwrapped reference is always truthy and made this guard dead code.
        if (!authStore.isAuthenticated()) {
            console.warn("Cannot connect to stream: not authenticated");
            return;
        }

        if (state.value.isConnected) {
            console.log("Stream already connected");
            return;
        }

        if (state.value.isConnecting) {
            console.log("Stream already connecting");
            return;
        }

        attachWakeListeners();
        clearRetry();
        const generation = connectGeneration;

        // Create connection promise to prevent race conditions
        connectionPromise = (async () => {
            state.value.isConnecting = true;
            state.value.lastError = null;

            // Tear down before the await below, or the old client's handlers
            // keep mutating state and connectionPromise while config loads.
            if (client.value) {
                console.log(
                    "🧹 Cleaning up existing client before reconnecting",
                );
                teardownClient();
            }

            let mqttConfig: MqttConfig;
            try {
                mqttConfig = await loadMqttConfig();
            } catch (error) {
                console.error("❌ Failed to fetch MQTT config:", error);
                state.value.lastError = `Failed to fetch MQTT config: ${describeConfigError(error)}`;
                state.value.isConnecting = false;
                connectionPromise = null;
                // No client exists yet, so MQTT.js can't retry for us.
                if (
                    generation === connectGeneration &&
                    isRetryableConfigError(error)
                ) {
                    scheduleRetry();
                }
                return;
            }

            // Disconnected or signed out while the config was loading.
            if (
                generation !== connectGeneration ||
                !authStore.isAuthenticated()
            ) {
                state.value.isConnecting = false;
                connectionPromise = null;
                return;
            }

            await new Promise<void>((resolve) => {
                const mqttUrl = getMqttUrl();
                console.log("🔌 Connecting to stream:", mqttUrl);

                // Simple connection like backend demo
                client.value = mqtt.connect(mqttUrl, {
                    username: mqttConfig.username,
                    password: mqttConfig.password,
                    clientId: `webui_${authStore.user?.id || "anonymous"}_${Date.now()}`,
                    clean: true,
                    // Let MQTT.js handle reconnection automatically
                    reconnectPeriod: 5000,
                    connectTimeout: 30000,
                    // Default 60s ties with 60s proxy idle timeouts (nginx, ALB),
                    // which cut quiet connections just before the first ping.
                    keepalive: 45,
                });

                // Attach event listeners to the new client
                eventListenersAttached = true;

                // Connection established
                client.value.on("connect", () => {
                    console.log("✅ Stream connected");
                    state.value.isConnected = true;
                    state.value.isConnecting = false;
                    state.value.lastError = null;
                    retryAttempt = 0;

                    // The cursor is deliberately NOT reset here — clearing it is
                    // what silently swallowed incidents raised during sleep.
                    if (hasConnectedBefore) {
                        // A reconnect means missed messages. Don't wait for a
                        // message to prove it; a quiet venue may not send one.
                        recoverStreamState(
                            mapContextStore,
                            "transport reconnected after a drop",
                            "stream-interrupted",
                        );
                    }
                    hasConnectedBefore = true;

                    // Guard: Check if client is still connected before subscribing
                    // This handles race conditions where connection closes immediately after connect event
                    if (!client.value || !client.value.connected) {
                        console.warn(
                            "⚠️ Client disconnected before subscription could complete",
                        );
                        connectionPromise = null;
                        resolve();
                        return;
                    }

                    // Subscribe to edge stream only
                    const subscriptions = {
                        [STREAM_TOPICS.edge]: { qos: 1 as const }, // Unified delta stream
                    };

                    client.value.subscribe(subscriptions, (err, granted) => {
                        if (err) {
                            // Only "connection closed" is benign. ErrorWithSubackPacket
                            // was also listed here, but MQTT.js builds it in exactly
                            // one place — a SUBACK reason code >= 0x80, i.e. the
                            // broker refusing us — and the socket then stays healthy,
                            // so nothing reconnects and we receive nothing forever.
                            const isConnectionClosedError =
                                err.message?.includes("Connection closed");

                            if (isConnectionClosedError) {
                                console.warn(
                                    "⚠️ Subscribe aborted: connection closed (will retry on reconnect)",
                                );
                            } else {
                                console.error(
                                    "❌ Subscribe rejected by broker — this client will receive no messages:",
                                    err,
                                );
                                state.value.lastError = `Subscribe error: ${err.message}`;
                            }
                        } else if (granted?.length) {
                            console.log(
                                "📡 Subscribed to streams:",
                                granted.map((g) => g.topic).join(", "),
                            );
                        } else {
                            // Empty `granted` does not mean "subscribed to nothing":
                            // MQTT.js resubscribes internally before our 'connect'
                            // listener runs, so this call was deduped to a no-op.
                            // The subscription is live.
                            console.log(
                                "📡 Subscription already active (resubscribed by client on reconnect):",
                                Object.keys(subscriptions).join(", "),
                            );
                        }
                    });

                    // Resolve the connection promise
                    connectionPromise = null;
                    resolve();
                });

                // Handle reconnection
                client.value.on("reconnect", () => {
                    console.log("🔄 Stream reconnecting...");
                    state.value.isConnecting = true;
                    state.value.isConnected = false;
                    // Cursor intentionally preserved — see the 'connect' handler.
                });

                // Connection closed
                client.value.on("close", () => {
                    console.log("🔒 Stream closed");
                    state.value.isConnected = false;
                    state.value.isConnecting = false;
                });

                // Connection error
                client.value.on("error", (error) => {
                    console.error("❌ Stream error:", error.message);
                    state.value.lastError = error.message;
                    state.value.isConnected = false;
                    state.value.isConnecting = false;

                    // Credentials may have been rotated: refetch them rather
                    // than let MQTT.js keep retrying the stale ones.
                    if (isBrokerAuthError(error)) {
                        mqttConfigPromise = null;
                        teardownClient();
                        scheduleRetry();
                    }

                    // Resolve the connection promise on error
                    if (connectionPromise) {
                        connectionPromise = null;
                        resolve();
                    }
                });

                // Handle messages - ensure only one listener per client
                client.value.on("message", handleMessage);
            });
        })().catch((error) => {
            // e.g. getMqttUrl() without a community context. Not retryable, but
            // must not leave connectionPromise rejected and isConnecting stuck.
            console.error("❌ Failed to start stream:", error);
            teardownClient();
            state.value.lastError =
                error instanceof Error ? error.message : String(error);
            state.value.isConnecting = false;
            connectionPromise = null;
        });

        return connectionPromise;
    };

    /**
     * Disconnect from MQTT broker
     */
    const disconnect = async () => {
        connectGeneration++;
        clearRetry();
        retryAttempt = 0;

        // If already connecting/disconnecting, wait for it to complete
        if (connectionPromise) {
            await connectionPromise;
        }

        // Again: the attempt we just awaited may have scheduled a retry.
        clearRetry();
        detachWakeListeners();
        mqttConfigPromise = null;

        if (!client.value) {
            console.log("No stream client to disconnect");
            return;
        }

        // No "already disconnected" state check: between MQTT.js reconnect
        // attempts both flags are false while the client is alive with a pending
        // timer, and bailing there left a client that resurrected after signout.
        // client.value is the real guard, and is nulled once teardown completes.
        console.log("👋 Disconnecting stream...");

        // Create disconnection promise
        connectionPromise = new Promise<void>((resolve) => {
            client.value?.end(true, {}, () => {
                console.log("✅ Stream disconnected");
                state.value.isConnected = false;
                state.value.isConnecting = false;
                client.value = null;
                eventListenersAttached = false; // Reset flag so new connections can attach listeners
                hasConnectedBefore = false; // Next connect is a fresh session, not a recovery
                _discardCoalescedLocations();
                resetStreamCursor(); // Session over — the cursor no longer means anything
                connectionPromise = null;
                resolve();
            });
        });

        return connectionPromise;
    };

    // Cleanup on unmount
    onUnmounted(() => {
        // Don't disconnect singleton on component unmount
        // Let AuthenticatedLayout handle lifecycle
    });

    return {
        // State
        state: computed(() => state.value),
        isStreamActive,
        mobileUserTimestamp: computed(() => mobileUserTimestamp.value),
        mqttServerTimestamp: computed(() => mqttServerTimestamp.value),

        // Backward compatibility
        lastMessageTimestamp: computed(() => mobileUserTimestamp.value),

        // Methods
        connect,
        disconnect,
        prefetchConfig,
    };
}

/**
 * Main export - returns singleton instance
 */
export function useStreamService() {
    if (!streamServiceInstance) {
        streamServiceInstance = createStreamService();
    }
    return streamServiceInstance;
}

/**
 * Reset singleton (for testing or cleanup)
 */
export async function resetStreamService() {
    if (streamServiceInstance) {
        await streamServiceInstance.disconnect();
        streamServiceInstance = null;
    }
    resetStreamCursor();
    // Only here can the Pinia instance behind the memoised stores go away.
    clearStreamStoreCache();
    connectionPromise = null;
    mqttConfigPromise = null;
    console.log("🔄 Stream service reset");
}
